/**
 * NinjaTraderBarSource — reads bar files written by the JournifiBars NT8 AddOn.
 *
 * Mirrors LocalCSVBarSource's structure but points at a separate root folder
 * (`C:\NinjaTrader8Data\JournifiBars\`) that the NT8 AddOn maintains. Higher
 * precedence than LocalCSVBarSource because NT-sourced bars are authoritative
 * and current, while {dataDir}/bars/ is a manual-drop fallback.
 *
 * Symbol-name normalization
 * -------------------------
 * NT writes folders by `Instrument.FullName` (e.g. `MNQ 03-26`), but
 * `trade.symbol` in the journal is just the root (`MNQ`). To bridge the two
 * we accept BOTH:
 *   - Exact-match path:      `<sym>/<dateStr>_<tf>.csv`
 *   - Prefix-match path:     any top-level folder whose name equals or starts
 *                            with `<sym> ` (case-insensitive) is a candidate.
 * Prefix matching means "MNQ" finds bars under `MNQ 03-26/`, `MNQ 06-26/`,
 * etc. We don't currently disambiguate between contract months — the AddOn
 * is expected to roll continuous-contract data forward into the active
 * folder. If/when multi-contract per-symbol becomes a real need, we'll
 * extend the request shape with a contract month.
 *
 * File format
 * -----------
 * Same CSV shape as LocalCSVBarSource — `csvBarParser.js` handles both.
 * The AddOn writes Unix-seconds timestamps; the parser is format-agnostic
 * so manual drops (e.g. NT's "Save As CSV") still work.
 *
 * Phase C v0: this source is in place ahead of the AddOn shipping. Drop a
 * test CSV at `C:\NinjaTrader8Data\JournifiBars\MNQ\2026-05-06_5m.csv` to
 * validate end-to-end before the NinjaScript side exists.
 */

import { parseCsvToBars } from '../csvBarParser.js';
import { aggregateBars, tfToMinutes } from '../aggregateBars.js';
import { getTradingDay } from '../../timeService.js';

/**
 * Extract the root symbol from a NinjaTrader-style folder/instrument name.
 *
 * NT names futures contracts in two common forms — the JournifiBars AddOn
 * preserves whatever the user typed in their settings.json, so we have to
 * recognize both:
 *
 *   - "MNQM6"       — contract code: <ROOT><MONTH><YEAR>
 *                     MONTH ∈ {F,G,H,J,K,M,N,Q,U,V,X,Z} (Jan..Dec).
 *                     Example: MNQM6 = MNQ + M(June) + 6(2026).
 *                     Note ROOT can contain digits (M2K = Micro Russell).
 *                     Non-greedy alpha-prefix match handles symbols like
 *                     MNQ that contain a month-code letter (Q=Aug) by
 *                     anchoring on the trailing digit requirement.
 *
 *   - "MNQ 06-26"   — full-name form with space + MM-YY.
 *
 *   - "MNQ"         — plain root, no contract month. Returns the input.
 *
 * Returns the upper-cased root symbol, or null if the input doesn't match
 * any recognized form (caller falls back to its own logic).
 */
const NT_CONTRACT_CODE_RE = /^([A-Z][A-Z0-9]*?)([FGHJKMNQUVXZ])(\d{1,2})$/i;
const NT_FULLNAME_RE      = /^([A-Z][A-Z0-9]*?)\s+(\d{1,2})-(\d{2})$/i;

const extractRootSymbol = (folderOrName) => {
  if (!folderOrName) return null;
  const trimmed = String(folderOrName).trim();
  if (!trimmed) return null;
  const m1 = trimmed.match(NT_FULLNAME_RE);
  if (m1) return m1[1].toUpperCase();
  const m2 = trimmed.match(NT_CONTRACT_CODE_RE);
  if (m2) return m2[1].toUpperCase();
  // Plain root (no contract month) — return uppercased input. Distinct from
  // null (unrecognized) so the caller can use this as the canonical root.
  if (/^[A-Z][A-Z0-9]*$/i.test(trimmed)) return trimmed.toUpperCase();
  return null;
};

export class NinjaTraderBarSource {
  constructor() {
    this.name = 'NinjaTrader (live)';
    // Lower numeric priority = higher precedence. NT-sourced bars beat the
    // universal LocalCSV fallback (priority 1000), so when both have data for
    // the same request, NT wins. Leaving room (1..49) for future broker-API
    // sources that should beat NT.
    this.priority = 50;
    this._available = null;
  }

  /**
   * Available iff the IPC bridge is wired AND the JournifiBars folder exists.
   *
   * Asymmetric caching:
   *   - Cache POSITIVE results forever (until invalidate). Once we know the
   *     AddOn is installed, IPC roundtrips on every fetchBars are wasted.
   *   - DON'T cache negative results. The folder may not exist yet because
   *     the user hasn't installed the AddOn, but they might install + start
   *     it mid-session — re-probing each time is cheap (one fs.existsSync
   *     in main process) and gives automatic recovery without forcing the
   *     user to hit Settings → Refresh. Time-budget: ~20μs per check.
   */
  async isAvailable() {
    if (this._available === true) return true;
    if (typeof window === 'undefined' || !window.electronAPI?.ntbars?.listFiles) {
      this._available = false;
      return false;
    }
    try {
      const res = await window.electronAPI.ntbars.listFiles();
      const ok = !!(res?.success && res?.dirExists);
      // Only memoize the positive case; leave _available at null on miss so
      // the next probe re-checks. Surfaces a freshly-installed AddOn within
      // one fetchBars call instead of requiring a manual Refresh.
      if (ok) this._available = true;
      else this._available = null;
      return ok;
    } catch {
      this._available = null;
      return false;
    }
  }

  describeStatus() {
    if (typeof window === 'undefined' || !window.electronAPI?.ntbars?.listFiles) {
      return { ready: false, message: 'NT bars IPC bridge unavailable (web build)' };
    }
    // _available === true   → confirmed AddOn folder exists
    // _available === null   → not yet probed OR last probe was negative
    //                        (we treat these the same in the UI: "not ready
    //                        yet" — the next fetchBars will re-probe)
    if (this._available !== true) {
      return {
        ready: false,
        message: 'JournifiBars AddOn not installed — install it in NT8 to auto-export bars',
      };
    }
    return { ready: true, message: 'Reading from C:\\NinjaTrader8Data\\JournifiBars\\' };
  }

  invalidateAvailability() {
    this._available = null;
  }

  /**
   * Locate file(s) for the given request. Returns an object describing the
   * resolution strategy, or null if no candidates found:
   *
   *   { mode: 'exact', path }      — single-day file, all rows are usable
   *   { mode: 'rolling', path }    — multi-day file, parsed bars must be sliced
   *                                  by the requested trading day
   *
   * Resolution order:
   *   1. Exact match in a symbol folder: `<sym>/<date>_<tf>.csv`, etc.
   *   2. Folder-prefix match for NT's contract-month folders: `MNQ 03-26/...`
   *   3. Rolling-file fallback: any .csv/.txt at root or in a symbol-prefixed
   *      folder. Used to consume NT's native "Save As" exports without the
   *      user having to slice them by day first.
   */
  async _resolvePath({ symbol, dateStr, timeframe }) {
    if (!window.electronAPI?.ntbars?.listFiles) return null;
    const res = await window.electronAPI.ntbars.listFiles();
    if (!res?.success) return null;
    // Normalize to a uniform shape: [{ path, mtimeMs }].
    // Older Electron-main versions (pre-v7.4.7) returned only `files` (string
    // array). Newer versions also return `filesWithMtime`. Fall back gracefully.
    const rawList = Array.isArray(res.filesWithMtime) && res.filesWithMtime.length > 0
      ? res.filesWithMtime
      : (res.files || []).map(f => ({ path: f, mtimeMs: 0 }));
    const files = rawList
      .map(f => ({ path: String(f.path).replace(/\\/g, '/'), mtimeMs: Number(f.mtimeMs) || 0 }))
      .filter(f => f.path);
    if (files.length === 0) return null;
    return this._resolveBestPath({ symbol, dateStr, timeframe, files });
  }

  /**
   * Pure resolution logic — given a normalized file list, pick the best
   * candidate. Extracted as a method so it can be tested via the exported
   * `resolveBestPath` wrapper at the bottom of this file.
   */
  _resolveBestPath({ symbol, dateStr, timeframe, files, nowMs = Date.now() }) {
    // Threshold for "this file is being actively written" — used to prefer
    // a rolling monthly file (live-updated) over a per-day file that was
    // written once and never touched again. 10 minutes covers the 5m bar
    // cadence + retry buffer; tune if needed.
    const FRESH_MS = 10 * 60 * 1000;
    const sym = String(symbol).toUpperCase();
    const fileByLower = new Map(files.map(f => [f.path.toLowerCase(), f]));
    const isFresh = (f) => f && (nowMs - f.mtimeMs) < FRESH_MS;

    // ── 1. Exact match (single-day per-TF CSV — what the AddOn will write) ─
    //      We still PREFER exact matches over rolling, but only when the
    //      exact match is itself fresh OR there's no fresher rolling
    //      candidate. The "fresher rolling beats stale per-day" check
    //      happens at step 3.
    const exact = [
      `${sym}/${dateStr}_${timeframe}.csv`,
      `${sym.toLowerCase()}/${dateStr}_${timeframe}.csv`,
      `${sym}/${dateStr}.csv`,
      `${sym.toLowerCase()}/${dateStr}.csv`,
    ];
    let exactMatch = null;
    for (const c of exact) {
      const hit = fileByLower.get(c.toLowerCase());
      if (hit) { exactMatch = hit; break; }
    }
    if (exactMatch && isFresh(exactMatch)) {
      return { mode: 'exact', path: exactMatch.path };
    }
    // Defer non-fresh exact match — let steps 2/3 look for a fresher rolling
    // file. We'll fall back to the stale exact below if nothing fresher exists.

    // ── 2. Folder-root match for per-day files in symbol-rooted subdirs.
    //      Recognizes three NT folder-naming conventions:
    //        - "MNQ 06-26/"  full-name form (space + contract month)
    //        - "MNQM6/"      contract code form (used by JournifiBars when
    //                        the user adds symbols by NT's coded name)
    //        - "MNQ/"        plain root form
    //      `extractRootSymbol` normalizes all three to the bare root.
    //      Like step 1, we PREFER fresh folder-root matches; defer non-fresh
    //      so step 3 can find a fresher rolling file.
    const wantedNames = [
      `${dateStr}_${timeframe}.csv`,
      `${dateStr}.csv`,
    ].map(s => s.toLowerCase());

    let folderMatch = null;
    for (const f of files) {
      const parts = f.path.split('/');
      if (parts.length < 2) continue;
      const folderRoot = extractRootSymbol(parts[0]);
      if (!folderRoot || folderRoot !== sym) continue;
      const rest = parts.slice(1).join('/').toLowerCase();
      if (wantedNames.includes(rest)) { folderMatch = f; break; }
    }
    if (folderMatch && isFresh(folderMatch)) {
      return { mode: 'exact', path: folderMatch.path };
    }

    // ── 3. Rolling-file fallback. Find any .csv/.txt whose filename or
    //      containing folder starts with the symbol root. Multi-day NT
    //      exports land here; we'll slice the parsed bars by trading day
    //      after reading.
    //
    //   File ranking (lower = better). Four signals stacked:
    //
    //     1. Freshness: a file modified within FRESH_MS is preferred over
    //        any older candidate. Without this, the resolver could latch
    //        onto a stale per-day file that was written once weeks ago,
    //        even when JournifiBars is actively writing a different rolling
    //        file. This is the v7.5.1 fix for the "chart 14m stale while
    //        JournifiSync NT shows last-write 0s ago" report.
    //
    //     2. Data-type tag in NT's manual exports:
    //          MNQ 06-26.Last.txt → strongly preferred (OHLC trades)
    //          MNQ 06-26.Bid.txt, .Ask.txt → fallback only
    //
    //     3. Year-month match against the requested trading day. JournifiBars
    //        writes monthly rolling files like MNQM6/2026-05_5m.csv — picking
    //        April's file when the user asks for May 7 means slicing returns
    //        zero bars (silent miss). Penalize wrong-month candidates heavily
    //        so the slice has data to work with.
    //
    //     4. Timeframe match. A 5m file is preferred over a 1m file for a 5m
    //        chart request — if we pick the 1m, the aggregator runs and
    //        produces correct 5m bars, but it's wasted work when an exact-TF
    //        file exists alongside.
    const targetYM = dateStr ? dateStr.slice(0, 7) : null; // "2026-05"
    const rankFile = (f) => {
      const lower = f.path.toLowerCase();
      const baseName = lower.split('/').pop().split('.')[0]; // e.g. "2026-05_5m"
      let score = 0;
      // Freshness wins decisively over all other signals. A fresh file
      // gets score -1000; a stale file gets score 0 (then other signals
      // add their penalties). This guarantees a live-written file beats
      // any combination of other "preferred" attributes on a stale file.
      if (isFresh(f)) score -= 1000;
      if (lower.includes('.last.')) score += 0;
      else if (lower.includes('.bid.') || lower.includes('.ask.')) score += 100;
      else score += 1; // untagged — assume Last
      if (targetYM && baseName.indexOf(targetYM) !== 0) score += 50;
      if (timeframe && !baseName.includes('_' + timeframe.toLowerCase())) score += 10;
      return score;
    };
    const candidates = [];
    for (const f of files) {
      const parts = f.path.split('/');
      const fileName = parts[parts.length - 1];
      const fileBase = fileName.split('.')[0]; // strip extension(s)
      const folder = parts.length > 1 ? parts[0] : '';

      // Match if EITHER the containing folder OR the filename root resolves
      // to the requested symbol via NT's naming conventions. Folder-side
      // catches AddOn output (`MNQM6/2026-05_1m.csv`); filename-side catches
      // root-level NT manual exports (`MNQ 06-26.Last.txt`).
      const folderRoot = extractRootSymbol(folder);
      const fileRoot   = extractRootSymbol(fileBase);
      if (folderRoot === sym || fileRoot === sym) {
        candidates.push(f);
      }
    }
    if (candidates.length > 0) {
      candidates.sort((a, b) => {
        // Primary: rank score (freshness + tags + month + tf).
        // Tiebreaker: mtimeMs DESC so the most-recently-written candidate
        // wins among files with identical rank.
        const ra = rankFile(a);
        const rb = rankFile(b);
        if (ra !== rb) return ra - rb;
        return b.mtimeMs - a.mtimeMs;
      });
      return { mode: 'rolling', path: candidates[0].path };
    }

    // Final fallback: a stale exact/folder match if no fresher rolling
    // candidate exists. Better to render SOMETHING than return null.
    if (exactMatch) return { mode: 'exact', path: exactMatch.path };
    if (folderMatch) return { mode: 'exact', path: folderMatch.path };

    return null;
  }

  /**
   * Slice a parsed bar array down to rows whose trading day matches the
   * requested `dateStr`. Trading-day boundary is the project-wide 6pm ET
   * boundary defined in `timeService.getTradingDay`.
   */
  _sliceByTradingDay(bars, dateStr) {
    const out = [];
    for (const b of bars) {
      // bar.time is Unix seconds; getTradingDay accepts ms epoch
      const td = getTradingDay(b.time * 1000);
      if (td === dateStr) out.push(b);
    }
    return out;
  }

  /**
   * Normalize NT bar timestamps from CLOSE-time to OPEN-time.
   *
   * NT's `Bars.GetTime(i)` returns the bar's CLOSING time for time-based
   * bars (well-documented quirk). JournifiBars writes that timestamp
   * directly. Downstream code (aggregation, MAE/MFE walk, chart rendering)
   * universally treats `bar.time` as the bar's OPENING time. The mismatch
   * means a 1-minute bar covering real time 11:55:00–11:55:59 is stamped
   * 11:56:00, and the bar walk's `t > exitTs` gate excludes it from any
   * trade exiting at 11:55, even though that bar is the one containing
   * the exit data.
   *
   * Fix: subtract one bar period from each timestamp at the source layer
   * so all downstream consumers get consistent open-time semantics. Period
   * is inferred from the median delta between successive timestamps —
   * robust against a missing bar here or there, doesn't require knowing
   * the timeframe upfront.
   *
   * Returns a new array; input bars are not mutated.
   */
  _normalizeNTTimestamps(bars) {
    if (!Array.isArray(bars) || bars.length < 2) return bars;
    const period = this._inferBarPeriodSec(bars);
    if (!period || period <= 0) return bars;
    return bars.map(b => ({ ...b, time: b.time - period }));
  }

  /**
   * Infer the bar period (in seconds) from successive timestamps. Uses
   * the median of consecutive deltas so a single dropped bar (or a 5pm-
   * 6pm session gap) doesn't skew the result.
   */
  _inferBarPeriodSec(bars) {
    if (!Array.isArray(bars) || bars.length < 2) return null;
    const deltas = [];
    for (let i = 1; i < bars.length; i++) {
      const d = bars[i].time - bars[i - 1].time;
      if (Number.isFinite(d) && d > 0) deltas.push(d);
    }
    if (deltas.length === 0) return null;
    deltas.sort((a, b) => a - b);
    const mid = Math.floor(deltas.length / 2);
    return deltas.length % 2 === 0
      ? Math.round((deltas[mid - 1] + deltas[mid]) / 2)
      : deltas[mid];
  }

  async fetchBars(req) {
    if (!(await this.isAvailable())) return null;
    const resolved = await this._resolvePath(req);
    if (!resolved) return null;
    const { mode, path } = resolved;

    try {
      const res = await window.electronAPI.ntbars.readFile(path);
      if (!res?.success || !res.text) return null;
      const { bars: rawBars, parsed, skipped, error } = parseCsvToBars(res.text, { label: path });
      if (error || rawBars.length === 0) {
        console.warn(`[NinjaTraderBarSource] ${path}: ${error || 'no rows parsed'} (parsed=${parsed}, skipped=${skipped})`);
        return null;
      }

      // NT writes close-time-stamped bars; normalize to open-time so the
      // rest of the journal (aggregation, MAE/MFE walk, chart rendering)
      // sees consistent semantics. Without this, the bar walk's exitTs
      // gate excludes the bar containing the trade's exit — which is the
      // root cause of the "Sharp entry on a stop-out" bug.
      const bars = this._normalizeNTTimestamps(rawBars);

      if (mode === 'rolling') {
        const sliced = this._sliceByTradingDay(bars, req.dateStr);
        if (sliced.length === 0) {
          console.log(`[NinjaTraderBarSource] ${path}: rolling file had no bars on trading day ${req.dateStr} (file has ${parsed} total bars)`);
          return null;
        }
        // NT's manual export only writes 1-minute bars. If the chart is asking
        // for 5m/15m/1h, downsample now so the chart gets bars at the right
        // granularity. Pure no-op when the request is 1m or unknown TF.
        const targetMin = tfToMinutes(req.timeframe);
        const finalBars = (targetMin && targetMin > 1) ? aggregateBars(sliced, targetMin) : sliced;
        const aggNote = (targetMin && targetMin > 1) ? ` → aggregated to ${finalBars.length} ${targetMin}m bars` : '';
        console.log(`[NinjaTraderBarSource] ${path}: rolling slice → ${sliced.length} 1m bars on ${req.dateStr}${aggNote} (file has ${parsed} total)`);
        return finalBars;
      }

      console.log(`[NinjaTraderBarSource] ${path}: ${parsed} bars parsed (${skipped} rows skipped)`);
      return bars;
    } catch (e) {
      console.warn(`[NinjaTraderBarSource] read failed for ${path}:`, e?.message || e);
      return null;
    }
  }
}

/**
 * Test wrapper around `NinjaTraderBarSource._resolveBestPath` so the pure
 * resolution logic can be exercised in node tests without Electron IPC.
 *
 * @param {Array<{path:string, mtimeMs:number}|string>} rawList
 *   List of available files. Accepts either rich `{path, mtimeMs}` objects
 *   or legacy bare-path strings (treated as mtime=0).
 * @param {Object} opts
 * @param {string} opts.symbol     Symbol to resolve.
 * @param {string} opts.dateStr    Trading day (YYYY-MM-DD).
 * @param {string} opts.timeframe  Bar timeframe (e.g. '5m').
 * @param {number} [opts.nowMs]    "Now" in ms for freshness math. Defaults to Date.now().
 * @returns {{ mode: 'exact'|'rolling', path: string } | null}
 */
export const resolveBestPath = (rawList, opts = {}) => {
  const files = (Array.isArray(rawList) ? rawList : [])
    .map(f => typeof f === 'string'
      ? { path: f, mtimeMs: 0 }
      : { path: String(f?.path || '').replace(/\\/g, '/'), mtimeMs: Number(f?.mtimeMs) || 0 })
    .filter(f => f.path);
  if (files.length === 0) return null;
  const inst = new NinjaTraderBarSource();
  return inst._resolveBestPath({ ...opts, files });
};
